import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSelectModule } from 'ng-zorro-antd/select';

import { IIdValue } from '../../types/id-value.interface';

type KorviuSelectValue = IIdValue['id'] | boolean | null | undefined;
type KorviuSelectSize = 'medium' | 'large';
type KorviuSelectPrefixType = 'sort' | 'filter';
type KorviuSelectSuffixType = 'country-flag-async';

@Component({
  selector: 'korviu-select',
  standalone: true,
  imports: [NgClass, NzFormModule, NzIconModule, NzSelectModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './korviu-select.component.html',
  styleUrls: ['./korviu-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class KorviuSelectComponent implements OnInit, OnChanges {
  readonly control = input<AbstractControl | null>(null);
  readonly customDropdown = input<boolean>(false);
  readonly disable = input<boolean>(false);
  readonly disabled = input<boolean | null>(null);
  readonly label = input<string>('');
  readonly options = input<IIdValue[]>([]);
  readonly placeholder = input<string>('');
  readonly prefixType = input<KorviuSelectPrefixType | undefined>();
  readonly showErrorMessagesUnderField = input<boolean>(false);
  readonly size = input<KorviuSelectSize>('medium');
  readonly suffixType = input<KorviuSelectSuffixType | undefined>();

  public open = false;

  public ngOnInit(): void {
    this.normalizeSelectedValue();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['control'] || changes['options']) {
      this.normalizeSelectedValue();
    }
  }

  public get formControl(): FormControl | null {
    return (this.control() as FormControl | null) ?? null;
  }

  public get isDisabled(): boolean {
    return this.disabled() ?? this.disable();
  }

  public get hasValue(): boolean {
    return this.valueIsPresent(this.formControl?.value);
  }

  public get isFilled(): boolean {
    return this.hasValue && !!this.formControl?.valid;
  }

  public get isInvalid(): boolean {
    return !!this.formControl?.dirty && !!this.formControl?.invalid;
  }

  public get resolvedPlaceholder(): string {
    return this.open ? this.placeholder() : '';
  }

  public get firstInputError(): ValidationErrors | null {
    const errors = this.formControl?.errors;
    const firstErrorKey = errors ? Object.keys(errors)[0] : null;

    return firstErrorKey ? { [firstErrorKey]: errors[firstErrorKey] } : null;
  }

  public fieldIsClearOrValid(field: FormControl | null): boolean {
    return (
      !field?.dirty || (field.dirty && field.valid) || field?.status === 'PENDING' || this.showErrorMessagesUnderField()
    );
  }

  public close(): void {
    this.open = false;
  }

  public getCountryFlagLink(countryId: IIdValue['id']): string {
    const countryCode = this.findOptionById(countryId)?.countryCode;

    return countryCode ? `https://purecatamphetamine.github.io/country-flag-icons/3x2/${countryCode}.svg` : '';
  }

  private normalizeSelectedValue(): void {
    const control = this.formControl;
    const options = this.options();

    if (!control || !options.length || !this.valueIsPresent(control.value)) {
      return;
    }

    if (!this.findOptionById(control.value)) {
      control.patchValue(options[0].id);
    }
  }

  private findOptionById(value: KorviuSelectValue): IIdValue | undefined {
    return this.options().find(option => this.idsAreEqual(option.id, value));
  }

  private idsAreEqual(left: IIdValue['id'], right: KorviuSelectValue): boolean {
    return left === right || String(left) === String(right);
  }

  private valueIsPresent(value: KorviuSelectValue): boolean {
    return value !== null && value !== undefined && value !== '';
  }
}
